import React, { useState, useEffect, useCallback, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import CircularProgress from '@material-ui/core/CircularProgress';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Toolbar from '@material-ui/core/Toolbar';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import Form from 'react-bootstrap/Form';
import PageTitle from '../../Utils/smsTitle';
import { SubHeader } from '../../../Containers/SubHeader';
import { surgeryService } from '../../../services/surgeryService';
import { UrgencyBadge } from '../shared';
import {
  URGENCY,
  URGENCY_LABELS,
  AGING_THRESHOLDS,
} from '../shared/constants';
import ReferralDetailPanel from './ReferralDetailPanel';
import Label from '../../Utils/FormInputs/Label';
import TextField from '../../Utils/FormInputs/TextField';
import SelectInput from '../../Utils/FormInputs/SelectInput';
import { useToolbarStyles } from '../../Accounting/FixedAssets/FixedAssetList';
import {
  CustomTableCell,
  MuiPaper,
  StickyHeadTable,
} from '../../Utils/Tables/StickyHeaderTable';
import { CustomTablePagination } from '../../Utils/Tables/CustomTable';

// ─── Helpers ────────────────────────────────────────────────────────────────

const URGENCY_PRIORITY = {
  [URGENCY.EMERGENCY]: 0,
  [URGENCY.URGENT]: 1,
  [URGENCY.ELECTIVE]: 2,
};

const getAgingInfo = (referral) => {
  const created = new Date(referral.created_at);
  const now = new Date();
  const diffMs = now - created;
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  const threshold = AGING_THRESHOLDS[referral.urgency];
  if (!threshold) {
    return { text: `${diffDays}d waiting`, isWarning: false, color: '#9E9E9E' };
  }

  if (referral.urgency === URGENCY.EMERGENCY) {
    const isWarning = diffHours >= (threshold.warningHours || 2);
    const text = diffHours < 1
      ? `${Math.floor(diffMs / (1000 * 60))} mins ago`
      : `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    return {
      text,
      isWarning,
      color: isWarning ? threshold.color : '#9E9E9E',
    };
  }

  const isWarning = diffDays > (threshold.warningDays || 14);
  const text = `${diffDays} day${diffDays !== 1 ? 's' : ''} waiting`;
  return {
    text,
    isWarning,
    color: isWarning ? threshold.color : '#9E9E9E',
  };
};

const sortReferrals = (referrals) => {
  return [...referrals].sort((a, b) => {
    const priorityA = URGENCY_PRIORITY[a.urgency] != null ? URGENCY_PRIORITY[a.urgency] : 99;
    const priorityB = URGENCY_PRIORITY[b.urgency] != null ? URGENCY_PRIORITY[b.urgency] : 99;
    if (priorityA !== priorityB) return priorityA - priorityB;
    return new Date(a.created_at) - new Date(b.created_at);
  });
};

const urgencyIntToKey = { 0: 'elective', 1: 'urgent', 2: 'emergency' };

// ─── Styles ─────────────────────────────────────────────────────────────────

const useStyles = makeStyles((theme) => ({
  root: {
    margin: 22,
  },
  contentLayout: {
    display: 'flex',
    gap: 16,
    alignItems: 'flex-start',
  },
  tableWrap: {
    flex: 1,
    minWidth: 0,
    transition: 'flex 0.2s ease',
  },
  sidePanel: {
    flex: '0 0 380px',
    maxWidth: 380,
    position: 'sticky',
    top: 80,
    maxHeight: 'calc(100vh - 200px)',
    border: '1px solid #EEEEEE',
    borderRadius: 8,
    backgroundColor: '#fff',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  sidePanelBody: {
    flex: 1,
    overflowY: 'auto',
  },
  sidePanelFooter: {
    padding: '12px 16px',
    borderTop: '1px solid #EEEEEE',
    backgroundColor: '#fff',
  },
  sidePanelHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 12px',
    borderBottom: '1px solid #EEEEEE',
  },
  sidePanelTitle: {
    fontSize: '0.8rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: '#9E9E9E',
  },
  tableRow: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#f5f5f5',
    },
  },
  selectedRow: {
    backgroundColor: '#E3F2FD !important',
  },
  loadingWrap: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 300,
  },
  agingText: {
    fontSize: '0.8rem',
    fontWeight: 500,
  },
}));

const MIN_ROWS = 10;

const headData = [
  { id: 'patient', label: 'Patient' },
  { id: 'procedure', label: 'Procedure' },
  { id: 'urgency', label: 'Urgency' },
  { id: 'pathway', label: 'Pathway' },
  { id: 'referring_doctor', label: 'Referring Doctor' },
  { id: 'waiting', label: 'Waiting' },
  { id: 'actions', label: 'Actions' },
];

// ─── Component ──────────────────────────────────────────────────────────────

const SurgeryBookingQueue = () => {
  const classes = useStyles();
  const toolbarClasses = useToolbarStyles ? useToolbarStyles() : {};

  const urgencyOptions = [
    { value: 'all', text: 'All Urgency' },
    { value: URGENCY.EMERGENCY, text: URGENCY_LABELS[URGENCY.EMERGENCY] },
    { value: URGENCY.URGENT, text: URGENCY_LABELS[URGENCY.URGENT] },
    { value: URGENCY.ELECTIVE, text: URGENCY_LABELS[URGENCY.ELECTIVE] },
  ];

  // Filter state
  const [urgencyFilter, setUrgencyFilter] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchDebounceRef = useRef(null);

  // Data state
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReferral, setSelectedReferral] = useState(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Cancel dialog state
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchInput(val);
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => setDebouncedSearch(val), 300);
  };

  // Fetch referrals
  const fetchReferrals = useCallback(() => {
    setLoading(true);
    const filters = {};
    if (urgencyFilter !== 'all') filters.urgency = urgencyFilter;
    if (debouncedSearch) filters.search = debouncedSearch;

    filters.per_page = 200;
    surgeryService
      .listReferrals(filters)
      .then((res) => {
        const data = res.data || res;
        const list = data.referrals || [];
        const pending = list.filter((r) => r.status === 0 || r.status === 1);
        setReferrals(sortReferrals(pending));
      })
      .catch((err) => {
        console.error('Failed to load referrals:', err);
        setReferrals([]);
      })
      .finally(() => setLoading(false));
  }, [urgencyFilter, debouncedSearch]);

  useEffect(() => {
    fetchReferrals();
  }, [fetchReferrals]);

  // Cancel referral handlers
  const handleCancelClick = (e, referral) => {
    e.stopPropagation();
    setCancelTarget(referral);
    setCancelReason('');
    setCancelDialogOpen(true);
  };

  const handleCancelConfirm = () => {
    if (!cancelTarget) return;
    setCancelling(true);
    surgeryService
      .cancelReferral({
        referral_id: cancelTarget.referral_id || cancelTarget.id,
        cancellation_reason: cancelReason,
        cancelled_by: 1,
      })
      .then(() => {
        setCancelDialogOpen(false);
        setCancelTarget(null);
        setCancelReason('');
        if (
          selectedReferral &&
          (selectedReferral.referral_id || selectedReferral.id) ===
            (cancelTarget.referral_id || cancelTarget.id)
        ) {
          setSelectedReferral(null);
        }
        fetchReferrals();
      })
      .catch(() => {})
      .finally(() => setCancelling(false));
  };

  const handleCancelClose = () => {
    setCancelDialogOpen(false);
    setCancelTarget(null);
    setCancelReason('');
  };

  const handleRowClick = (referral) => {
    setSelectedReferral(referral);
  };

  const handleClosePanel = () => {
    setSelectedReferral(null);
  };

  // Pagination
  const handleChangePage = (_e, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const paginated = referrals.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Empty rows to fill table to consistent height
  const emptyRowCount = Math.max(0, MIN_ROWS - paginated.length);

  const pagination = (
    <CustomTablePagination
      colSpan={3}
      page={page}
      rowsPerPage={rowsPerPage}
      count={referrals.length}
      handleChangePage={handleChangePage}
      handleChangeRowsPerPage={handleChangeRowsPerPage}
      isTotalCount
    />
  );

  return (
    <div>
      <PageTitle title="Booking Queue" />
      <SubHeader title="Surgery" subTitle="Booking Queue" />

      <div className={classes.root}>
        <div className={classes.contentLayout}>
          {/* Table */}
          <div className={classes.tableWrap}>
            {loading ? (
              <div className={classes.loadingWrap}>
                <CircularProgress />
              </div>
            ) : (
              <MuiPaper>
                <Toolbar className={clsx(toolbarClasses.root)}>
                  <div className="col-md-4 col-lg-4 col-sm-12" style={{ paddingLeft: 0 }}>
                    <TextField
                      onChange={handleSearchChange}
                      type="text"
                      value={searchInput}
                      name="search"
                      id="searchBookingQueue"
                      placeholder="Search patient name or number..."
                    />
                  </div>
                  <div className="col-md-3 col-lg-2 col-sm-12" style={{ paddingLeft: 0, marginLeft: 16 }}>
                    <SelectInput
                      value={urgencyFilter}
                      onChange={(e) => {
                        setUrgencyFilter(e.target.value);
                        setPage(0);
                      }}
                      options={urgencyOptions}
                      name="urgencyFilter"
                    />
                  </div>
                  <div className={toolbarClasses.spacer}></div>
                  <Typography style={{ fontSize: '0.85rem', color: '#666', whiteSpace: 'nowrap' }}>
                    {referrals.length} pending
                  </Typography>
                </Toolbar>
                <StickyHeadTable
                  width="100%"
                  pagination={pagination}
                  data={paginated}
                  columns={headData}
                >
                  {referrals.length > 0 ? (
                    <>
                      {paginated.map((referral) => {
                        const referralId = referral.referral_id || referral.id;
                        const patientName =
                          referral.patient_name ||
                          `${referral.patient_first_name || ''} ${referral.patient_last_name || ''}`.trim();
                        const referringDoctor =
                          referral.referring_doctor ||
                          `${referral.referring_doctor_first_name || ''} ${referral.referring_doctor_last_name || ''}`.trim();
                        const procedureName =
                          referral.procedure_requested || referral.service_name || '-';
                        const urgencyKey = urgencyIntToKey[referral.urgency] ?? referral.urgency;
                        const aging = getAgingInfo(referral);
                        const isSelected =
                          selectedReferral &&
                          (selectedReferral.referral_id || selectedReferral.id) === referralId;

                        return (
                          <TableRow
                            key={referralId}
                            className={`${classes.tableRow} ${isSelected ? classes.selectedRow : ''}`}
                            onClick={() => handleRowClick(referral)}
                          >
                            <CustomTableCell>
                              <Typography variant="body2" style={{ fontWeight: 600 }}>
                                {patientName}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {referral.patient_number}
                              </Typography>
                            </CustomTableCell>
                            <CustomTableCell>{procedureName}</CustomTableCell>
                            <CustomTableCell>
                              <UrgencyBadge urgency={urgencyKey} />
                            </CustomTableCell>
                            <CustomTableCell>
                              {referral.pathway || '-'}
                            </CustomTableCell>
                            <CustomTableCell>
                              {referringDoctor || '-'}
                              {referral.referring_department
                                ? ` (${referral.referring_department})`
                                : ''}
                            </CustomTableCell>
                            <CustomTableCell>
                              <Typography
                                className={classes.agingText}
                                style={{ color: aging.color }}
                              >
                                {aging.text}
                              </Typography>
                            </CustomTableCell>
                            <CustomTableCell onClick={(e) => e.stopPropagation()}>
                              <Link
                                to={`/surgery/schedule/${referralId}`}
                                style={{ textDecoration: 'none' }}
                              >
                                <button className="btn btn-sm sms-info-btn mr-1">
                                  Schedule
                                </button>
                              </Link>
                              <button
                                className="btn btn-sm sms-gray-btn"
                                onClick={(e) => handleCancelClick(e, referral)}
                              >
                                Cancel
                              </button>
                            </CustomTableCell>
                          </TableRow>
                        );
                      })}
                      {/* Empty rows for consistent table height */}
                      {emptyRowCount > 0 &&
                        Array.from({ length: emptyRowCount }).map((_, idx) => (
                          <TableRow key={`empty-${idx}`} style={{ height: 53 }}>
                            {headData.map((col) => (
                              <CustomTableCell key={col.id}>&nbsp;</CustomTableCell>
                            ))}
                          </TableRow>
                        ))}
                    </>
                  ) : (
                    <TableRow>
                      <TableCell align="center" colSpan={headData.length}>
                        No pending referrals found
                      </TableCell>
                    </TableRow>
                  )}
                </StickyHeadTable>
              </MuiPaper>
            )}
          </div>

          {/* Side Panel — only renders when a row is selected */}
          {selectedReferral && (
            <div className={classes.sidePanel}>
              <div className={classes.sidePanelHeader}>
                <Typography className={classes.sidePanelTitle}>
                  Referral Details
                </Typography>
                <IconButton size="small" onClick={handleClosePanel}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </div>
              <div className={classes.sidePanelBody}>
                <ReferralDetailPanel referral={selectedReferral} hideActions />
              </div>
              <div className={classes.sidePanelFooter}>
                <Link
                  to={`/surgery/schedule/${selectedReferral.referral_id || selectedReferral.id}`}
                  style={{ textDecoration: 'none' }}
                >
                  <button className="btn sms-info-btn w-100">
                    Schedule Surgery
                  </button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Cancel Confirmation Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={handleCancelClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Cancel Referral</DialogTitle>
        <DialogContent>
          {cancelTarget && (
            <Typography variant="body2" style={{ marginBottom: 16, color: '#616161' }}>
              Cancel referral for{' '}
              <strong>
                {cancelTarget.patient_name ||
                  `${cancelTarget.patient_first_name || ''} ${cancelTarget.patient_last_name || ''}`.trim() ||
                  cancelTarget.patient_number}
              </strong>{' '}
              ({cancelTarget.patient_number}) &mdash;{' '}
              {cancelTarget.service_name || cancelTarget.procedure_requested || 'this referral'}?
            </Typography>
          )}
          <Form.Group>
            <Label name="Cancellation Reason" />
            <TextField
              autoFocus
              as="textarea"
              rows={3}
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Please provide a reason for cancellation"
            />
          </Form.Group>
        </DialogContent>
        <DialogActions>
          <button className="btn btn-sm sms-info-btn mr-2" onClick={handleCancelClose} disabled={cancelling}>
            Back
          </button>
          <button
            className="btn btn-sm sms-gray-btn"
            onClick={handleCancelConfirm}
            disabled={!cancelReason.trim() || cancelling}
          >
            {cancelling ? 'Cancelling...' : 'Confirm Cancel'}
          </button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default SurgeryBookingQueue;
